import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import TableIcon from '@atlaskit/icon/core/table';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { onRovoNavigationClicked } from '@atlassian/search-common/rovo-navigation-interacted';

import { serveJiraNinFpsNavigation } from '../../../common/utils/experiments/experiment-exports';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useSainJql } from '../../../controllers/smart-answers-intent-store';
/* eslint-disable @atlassian/tangerine/import/no-restricted-paths */
import {
	useIsHello,
	usePrimaryProduct,
	useSearchSessionId,
} from '../../../controllers/store/bootstrap';
/* eslint-enable @atlassian/tangerine/import/no-restricted-paths */
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useSearchQuery } from '../../../controllers/store/search';
import { sha256Hash } from '../../utils/events/sha256-hash';
import { isJqlQueryDetected } from '../../utils/intent-detection';
import { buildJql } from '../../utils/quick-find/jira-work-item-navigation';

import { i18n } from './messages';

const REFERRER_PARAM = 'referrer=search-page';

// This is the default url when you use "Enter" on QF in Jira
const NEW_ISSUE_NAVIGATOR_DEFAULT_URL = `/issues/?jql=${encodeURIComponent(
	'created >= -30d order by created DESC',
)}&${REFERRER_PARAM}`;

const styles = cssMap({
	buttonLinkStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		color: token('color.text.subtle'),
		borderRadius: token('radius.small'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.050'),
		maxHeight: '32px',
		minHeight: '32px',
		backgroundColor: token('color.background.neutral.subtle'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		whiteSpace: 'nowrap',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		maxWidth: '280px',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:visited': {
			color: token('color.text.subtle'),
		},
	},
});

export const NewIssueNavigatorCTA = (): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const [searchQuery] = useSearchQuery();
	const [isHello] = useIsHello();
	const [searchSessionId] = useSearchSessionId();
	const [primaryProduct] = usePrimaryProduct();
	const sainJql = useSainJql();
	const isPartOfJiraNinFpsExperiment = useMemo(
		() =>
			serveJiraNinFpsNavigation({ primaryProduct: primaryProduct, locale })?.cohort === 'control',
		[primaryProduct, locale],
	);

	const referrer = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		return params.get('referrer') || '';
	}, []);

	const href = useMemo(() => {
		const trimmedQuery = searchQuery.trim();

		try {
			if (sainJql) {
				return `/issues/?jql=${encodeURIComponent(sainJql)}&${REFERRER_PARAM}`;
			}
			if (trimmedQuery !== '') {
				let jql;
				const typedJqlRoutingEnabled = fg('rovo_fps_route_typed_jql_as_jql');
				if (typedJqlRoutingEnabled && isJqlQueryDetected(searchQuery)) {
					jql = encodeURIComponent(trimmedQuery);
				} else {
					jql = buildJql({ query: searchQuery, projectARIs: [], accountARIs: [] });
				}
				return `/issues/?jql=${jql}&${REFERRER_PARAM}`;
			}
			return NEW_ISSUE_NAVIGATOR_DEFAULT_URL;
		} catch {
			return NEW_ISSUE_NAVIGATOR_DEFAULT_URL;
		}
	}, [searchQuery, sainJql]);

	const onClick = useCallback(() => {
		fireAnalyticsEvent(
			onRovoNavigationClicked({
				source: 'advancedSearchScreen',
				experiment: isPartOfJiraNinFpsExperiment ? 'control' : undefined,
				commonAttributes,
				queryHash: sha256Hash(searchQuery),
				queryLength: searchQuery.length,
				isHello,
				searchSessionId,
				referrer,
			}),
		);
	}, [
		fireAnalyticsEvent,
		isPartOfJiraNinFpsExperiment,
		commonAttributes,
		searchQuery,
		isHello,
		searchSessionId,
		referrer,
	]);

	return (
		<Anchor
			onClick={onClick}
			xcss={styles.buttonLinkStyles}
			href={href}
			testId="issue-navigator-cta"
		>
			{/* The button label contains description of the button */}
			<TableIcon label="" />
			{formatMessage(i18n.buttonLabel)}
		</Anchor>
	);
};
